import { type ReactNode, useMemo } from 'react';

import {
  ComponentAnalyticsProvider as SurfaceComponentAnalyticsProvider,
  type ComponentAnalyticsTrackFn,
} from '@surface/components';

import { useAnalytics } from './use-analytics';

import type { TrackFn } from './types';

/**
 * Wrapper function that adapts the typed TrackFn to the generic ComponentAnalyticsTrackFn
 * expected by the component library context.
 */
const adaptTrackFn = (track: TrackFn): ComponentAnalyticsTrackFn => {
  return (eventName: string, payload?: Record<string, unknown>) => {
    // Type assertion is safe here because we're wrapping our typed function.
    // The component library will call this with valid event names, and we trust
    // that the consuming app's analytics system can handle them.
    track(eventName as Parameters<TrackFn>[0], payload);
  };
};

/**
 * Provider component that makes the analytics track function available to all
 * child components via React Context. Component library components can use the
 * `useComponentAnalytics` hook to access this function.
 *
 * This allows us to bake in tracking functions into the component library components without
 * requiring the consumer to pass a tracking function as a prop or explicitly define each tracking
 * call in every instance of the component used.
 *
 * This provider should be added near the root of your application (e.g., in App.tsx)
 * to enable automatic tracking in component library components.
 *
 * @example
 * ```tsx
 * function App() {
 *   return (
 *     <ComponentAnalyticsProvider>
 *       <YourApp />
 *     </ComponentAnalyticsProvider>
 *   );
 * }
 * ```
 */
export const ComponentAnalyticsProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { track } = useAnalytics();

  const contextValue = useMemo(() => {
    const adapted = adaptTrackFn(track);
    if (import.meta.env.DEV) {
      console.log(
        '[ComponentAnalyticsProvider] Providing track function to context',
        {
          trackAvailable: !!track,
          adaptedAvailable: !!adapted,
        }
      );
    }
    return adapted;
  }, [track]);

  return (
    <SurfaceComponentAnalyticsProvider value={contextValue}>
      {children}
    </SurfaceComponentAnalyticsProvider>
  );
};
